/**
 * TheatricalModeChip - Subtle Viewing Mode Toggle Component
 *
 * Bottom-left positioned chip that allows users to toggle between viewing modes.
 * Positioned non-intrusively during analysis animations.
 *
 * Accessibility:
 * - role="switch" for screen readers
 * - Accessible labels for each mode
 * - Respects reduced motion (hides chip)
 *
 * Visual Design:
 * - Y2K gradient styling with glassmorphism
 * - Smooth fade-in animation
 * - Subtle pulse effect on mode change
 *
 * Usage:
 * ```tsx
 * import { TheatricalModeChip } from '@/components/theatrical/TheatricalModeChip';
 *
 * function AnalysisScreen() {
 *   const { variant, setMode } = useTheatricalMode();
 *
 *   const handleToggle = async () => {
 *     const nextMode = variant === 'quick' ? 'theatrical' : 'quick';
 *     await setMode(nextMode);
 *   };
 *
 *   return (
 *     <View>
 *       <LetterZoomAnimation {...props} />
 *       <TheatricalModeChip
 *         variant={variant}
 *         onToggle={handleToggle}
 *       />
 *     </View>
 *   );
 * }
 * ```
 */

import React, { useEffect } from 'react';
import { Pressable, Text, View, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { createBoxShadow, createTextShadow } from '@/lib/design-tokens';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  Easing,
} from 'react-native-reanimated';
import { useReducedMotionWithLoading } from '@/hooks/useReducedMotionWithLoading';
import type { TheatricalModeVariant } from '@/lib/theatrical/theatricalModeConfig';

/**
 * Props for TheatricalModeChip
 */
export interface TheatricalModeChipProps {
  /** Current theatrical mode variant */
  variant: TheatricalModeVariant;

  /** Callback when user toggles mode */
  onToggle: () => void;

  /** Whether to show the chip (default: true, hidden during animation) */
  visible?: boolean;
}

/**
 * Mode display configuration
 */
const MODE_CONFIG: Record<TheatricalModeVariant, { label: string; emoji: string }> = {
  quick: { label: 'Skip to Results', emoji: '⚡' },
  theatrical: { label: 'Enjoy the Show', emoji: '🎭' },
  dramax_premium: { label: 'VIP Experience', emoji: '👑' },
};

/**
 * TheatricalModeChip Component
 *
 * Subtle bottom-left chip for toggling viewing mode (entertainment experience variants).
 */
export function TheatricalModeChip({
  variant,
  onToggle,
  visible = true,
}: TheatricalModeChipProps) {
  const reducedMotion = useReducedMotionWithLoading();

  // Animation values
  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.9);
  const pulseScale = useSharedValue(1);

  // Hide chip for users with reduced motion (they're always in quick mode)
  if (reducedMotion) {
    return null;
  }

  // Fade in on mount
  useEffect(() => {
    if (visible) {
      opacity.value = withTiming(1, { duration: 300, easing: Easing.out(Easing.ease) });
      scale.value = withTiming(1, { duration: 300, easing: Easing.out(Easing.back(1.5)) });
    } else {
      opacity.value = withTiming(0, { duration: 200, easing: Easing.in(Easing.ease) });
      scale.value = withTiming(0.9, { duration: 200, easing: Easing.in(Easing.ease) });
    }
  }, [visible, opacity, scale]);

  // Pulse effect on mode change
  useEffect(() => {
    pulseScale.value = withSequence(
      withTiming(1.1, { duration: 150, easing: Easing.out(Easing.ease) }),
      withTiming(1.0, { duration: 150, easing: Easing.in(Easing.ease) })
    );
  }, [variant, pulseScale]);

  // Animated styles
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [
      { scale: scale.value },
      { scale: pulseScale.value },
    ],
  }));

  const config = MODE_CONFIG[variant];

  return (
    <Animated.View style={[styles.container, animatedStyle]}>
      <Pressable
        onPress={onToggle}
        style={({ pressed }) => [
          styles.chip,
          pressed && styles.chipPressed,
        ]}
        accessibilityRole="switch"
        accessibilityState={{ checked: variant !== 'quick' }}
        accessibilityLabel={`Viewing mode: ${config.label}. Tap to toggle.`}
        accessibilityHint="Changes viewing experience style"
      >
        <View style={styles.chipContent}>
          <Text style={styles.emoji}>{config.emoji}</Text>
          <Text style={styles.label}>{config.label}</Text>
        </View>
      </Pressable>
    </Animated.View>
  );
}

// Platform-aware shadows
const chipShadow = createBoxShadow({
  color: '#FFB3E6',
  offsetX: 0,
  offsetY: 2,
  blurRadius: 8,
  opacity: 0.25,
  elevation: 4,
});

const labelTextShadow = createTextShadow({
  color: 'rgba(0, 0, 0, 0.4)',
  offsetX: 0,
  offsetY: 1,
  blurRadius: 2,
});

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 24,
    left: 16,
    zIndex: 100,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    // Y2K gradient backdrop (glassmorphism)
    ...chipShadow,
  } as ViewStyle,
  chipPressed: {
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
    transform: [{ scale: 0.95 }],
  },
  chipContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  emoji: {
    fontSize: 16,
    lineHeight: 16,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FFFFFF',
    letterSpacing: 0.3,
    // Y2K text shadow for readability
    ...labelTextShadow,
  } as TextStyle,
});
